import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Building2, LogOut, Loader2, AlertTriangle, ArrowRight, Bell,
  FileText, Clock, Activity, RefreshCw, TrendingUp, Layers
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PushToggleButton } from '@/components/PushToggleButton';

/**
 * CogniSeg Global Dashboard.
 *
 * Consolidated triage-service view for the single global CogniSeg user. Shows:
 *  - Global KPIs across every tenant it has access to
 *  - A rich card per tenant with per-status counts + overdue + latest report
 *  - "Enter" action per tenant (exchanges the JWT for a tenant-scoped one via
 *    POST /api/cogniseg/select-tenant and routes into /admin).
 */
const ChooseTenantPage = () => {
  const { user, logout, setTenantContext } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [entering, setEntering] = useState('');
  const [error, setError] = useState('');

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setError('');
    try {
      const { data: res } = await api.get('/cogniseg/dashboard');
      setData(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // Sug 3 — real-time-ish: silent polling every 60s + flash a badge if new
  // reports arrived since the last fetch.
  const [newSinceLast, setNewSinceLast] = useState(0);
  useEffect(() => {
    if (!data) return;
    const baseline = data.global.reports_total;
    const interval = setInterval(async () => {
      try {
        const { data: res } = await api.get('/cogniseg/dashboard');
        const delta = (res.global?.reports_total || 0) - baseline;
        if (delta > 0) {
          setNewSinceLast(delta);
          setData(res);
        }
      } catch (_) { /* silent */ }
    }, 60000);
    return () => clearInterval(interval);
  }, [data]);

  const handleEnter = async (tenant) => {
    setEntering(tenant.id);
    setError('');
    try {
      const { data: res } = await api.post('/cogniseg/select-tenant', { tenant_id: tenant.id });
      setTenantContext(res?.tenant || tenant);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao entrar no cliente');
      setEntering('');
    }
  };

  const stats = data?.global || { tenants_count: 0, reports_total: 0, reports_pending: 0, reports_overdue: 0, reports_this_week: 0, reports_this_month: 0 };
  const tenants = data?.tenants || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-slate-100">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-indigo-600/20 border border-indigo-500/30">
              <Shield className="w-6 h-6 text-indigo-300" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-indigo-300/80">CogniSeg · Triage as a Service</div>
              <h1 className="text-2xl font-semibold text-white flex items-center gap-3">
                Olá, {user?.name || 'Gestor de Triagem'}
                {newSinceLast > 0 && (
                  <button
                    onClick={() => { setNewSinceLast(0); fetchData(true); }}
                    className="text-xs font-medium px-3 py-1 rounded-full bg-red-500/20 border border-red-400/40 text-red-100 animate-pulse hover:bg-red-500/30"
                    data-testid="new-reports-badge"
                  >
                    +{newSinceLast} nova{newSinceLast === 1 ? '' : 's'} denúncia{newSinceLast === 1 ? '' : 's'}
                  </button>
                )}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PushToggleButton />
            <Button
              onClick={() => navigate('/cogniseg/denuncias')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="open-bulk-actions-btn"
            >
              <Layers className="w-4 h-4 mr-2" />
              Ações em massa
            </Button>
            <Button
              variant="ghost"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="text-slate-300 hover:text-white hover:bg-white/5"
              data-testid="cogniseg-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button
              variant="ghost"
              onClick={async () => { await logout(); navigate('/login'); }}
              className="text-slate-300 hover:text-white hover:bg-white/5"
              data-testid="cogniseg-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>

        {error && (
          <Alert className="mb-6 bg-red-900/30 border-red-500/40 text-red-100">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Global KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="cogniseg-kpis">
          <KpiCard label="Clientes" value={stats.tenants_count} icon={Building2} accent="text-slate-100" />
          <KpiCard label="Denúncias totais" value={stats.reports_total} icon={FileText} accent="text-slate-100" />
          <KpiCard label="Pendentes" value={stats.reports_pending} icon={Layers} accent="text-amber-300" testid="kpi-pending" />
          <KpiCard label="Prazos vencidos" value={stats.reports_overdue} icon={Clock} accent="text-red-300" testid="kpi-overdue" />
          <KpiCard label="Últimos 7 dias" value={stats.reports_this_week} icon={TrendingUp} accent="text-emerald-300" />
          <KpiCard label="Este mês" value={stats.reports_this_month} icon={Activity} accent="text-indigo-300" />
        </div>

        <div className="mb-6">
          <h2 className="text-lg font-medium text-white">Clientes onde opera</h2>
          <p className="text-sm text-slate-400 mt-1">
            Clique num cliente para entrar e gerir as denúncias. A ordenação prioriza clientes com prazos vencidos e pendentes.
          </p>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-slate-300" data-testid="cogniseg-loading">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>A carregar…</span>
          </div>
        )}

        {!loading && tenants.length === 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center">
              <Building2 className="w-10 h-10 mx-auto text-slate-500 mb-3" />
              <div className="text-white font-medium">Ainda não tem clientes atribuídos</div>
              <p className="text-sm text-slate-400 mt-1">
                Quando um novo tenant for provisionado em modo CogniSeg, aparecerá aqui automaticamente.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && tenants.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cogniseg-tenants-grid">
            {tenants.map(t => (
              <TenantCard
                key={t.id}
                tenant={t}
                busy={entering === t.id}
                onEnter={() => handleEnter(t)}
              />
            ))}
          </div>
        )}

        <div className="mt-10 text-xs text-slate-500 text-center">
          Sessão global CogniSeg · <span className="text-slate-400">{user?.email}</span>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, icon: Icon, accent = 'text-slate-100', testid }) => (
  <Card className="bg-white/5 border-white/10" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className={`mt-2 text-2xl font-semibold ${accent}`}>{value}</div>
    </CardContent>
  </Card>
);

const TenantCard = ({ tenant: t, busy, onEnter }) => {
  const overdue = t.reports_overdue || 0;
  const pending = t.reports_pending || 0;
  const initial = (t.name || '?').slice(0, 1).toUpperCase();
  const lastReport = t.last_report_at ? new Date(t.last_report_at) : null;
  const relativeLast = lastReport ? formatRelative(lastReport) : null;

  return (
    <Card
      className={`group border transition cursor-pointer overflow-hidden
        ${overdue > 0
          ? 'bg-red-950/30 border-red-500/40 hover:border-red-400/70 hover:bg-red-950/50'
          : pending > 0
            ? 'bg-amber-950/20 border-amber-500/30 hover:border-amber-400/60 hover:bg-amber-950/30'
            : 'bg-white/5 border-white/10 hover:border-indigo-400/60 hover:bg-white/10'
        }`}
      onClick={() => !busy && onEnter()}
      data-testid={`cogniseg-tenant-${t.slug}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold ring-1 ring-white/10"
              style={{ background: t.primary_color || '#0F172A' }}
            >
              {initial}
            </div>
            <div>
              <div className="text-white font-medium leading-tight">{t.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">/{t.slug} · plano {t.plan || '—'}</div>
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end">
            {overdue > 0 && (
              <Badge className="bg-red-500/20 text-red-200 border-red-400/40 hover:bg-red-500/30">
                <Clock className="w-3 h-3 mr-1" />{overdue} vencidos
              </Badge>
            )}
            {pending > 0 && (
              <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 hover:bg-amber-500/30">
                <Bell className="w-3 h-3 mr-1" />{pending} pendentes
              </Badge>
            )}
          </div>
        </div>

        {/* Per-status breakdown */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <MiniStat label="Recebidas" value={t.reports_received} />
          <MiniStat label="Em triagem" value={t.reports_in_triage} />
          <MiniStat label="Investig." value={t.reports_in_investigation} />
        </div>

        {/* Recent activity + entering CTA */}
        <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-slate-400">
          <div>
            {relativeLast ? (
              <>
                Última: <span className="text-slate-200 font-medium">{t.last_report_tracking}</span> · {relativeLast}
              </>
            ) : (
              <span className="text-slate-500">Sem denúncias ainda</span>
            )}
          </div>
          <div className="text-indigo-300 flex items-center gap-1 group-hover:translate-x-0.5 transition text-sm">
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> A entrar…</>
            ) : (
              <>Entrar <ArrowRight className="w-4 h-4" /></>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="bg-white/5 rounded-md py-2 text-center border border-white/5">
    <div className="text-lg font-semibold text-slate-100">{value ?? 0}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
  </div>
);

function formatRelative(date) {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} d`;
  return date.toLocaleDateString('pt-PT');
}

export default ChooseTenantPage;
